import React, { Dispatch, SetStateAction, useState } from 'react';
import { useFormikContext } from 'formik';
import {
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    styled,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { ConfirmationDialog } from '@fnol/library/src/components';
import { translations } from '../../lib/consts';
import { RoomCleanup } from '../Room/Room';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const createData = (type: string, area: string, editable: boolean, editId: number) => {
    return { type, area, editable, editId };
};

type GenericObject = { [key: string]: any };

interface DamagedRoomsTableProps {
    inputRoomOpen: boolean;
    setInputRoomOpen: Dispatch<SetStateAction<boolean>>;
    setInputRoomId: Dispatch<SetStateAction<number>>;
    setAddPossible: Dispatch<SetStateAction<boolean>>;
}

const DamagedRoomsTable = ({
    inputRoomOpen,
    setInputRoomOpen,
    setInputRoomId,
    setAddPossible,
}: DamagedRoomsTableProps) => {
    const { values, setFieldValue } = useFormikContext<GenericObject>();
    let editIds = [0];
    if (values.damagedRooms && values.damagedRooms === 1) editIds = [1];
    else if (values.damagedRooms && values.damagedRooms === 2) editIds = [1, 2];
    else if (values.damagedRooms && values.damagedRooms === 3) editIds = [1, 2, 3];

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(0);

    const rows: GenericObject[] = [];

    const copyDamagedRoomToId = (idFrom: number, idTo: number) => {
        setFieldValue(`room${idTo}Type`, values[`room${idFrom}Type`]);
        setFieldValue(`room${idTo}Area`, values[`room${idFrom}Area`]);
        setFieldValue(`room${idTo}IsDamageOnCeiling`, values[`room${idFrom}IsDamageOnCeiling`]);
        setFieldValue(`room${idTo}NumberOfDemagedWalls`, values[`room${idFrom}NumberOfDemagedWalls`]);
        setFieldValue(`room${idTo}WallFinishType`, values[`room${idFrom}WallFinishType`]);
        setFieldValue(`room${idTo}Note`, values[`room${idFrom}Note`]);
    };

    const handleEditDamagedRoom = (editId: number) => {
        setFieldValue('roomType', values[`room${editId}Type`]);
        setFieldValue('roomArea', values[`room${editId}Area`]);
        setFieldValue('roomIsDamageOnCeiling', values[`room${editId}IsDamageOnCeiling`]);
        setFieldValue('roomNumberOfDemagedWalls', values[`room${editId}NumberOfDemagedWalls`]);
        setFieldValue('roomWallFinishType', values[`room${editId}WallFinishType`]);
        setFieldValue('roomNote', values[`room${editId}Note`]);
        setInputRoomId(editId);
        setInputRoomOpen(true);
    };

    const handleDeleteDamagedRoom = (deleteId: number) => {
        editIds.map((editId) => {
            if (editId === deleteId) {
                RoomCleanup(setFieldValue, deleteId);
                setAddPossible(true);
                let counter = editId;
                while (counter <= editIds.length) {
                    const oneUp = counter + 1;
                    if (values[`room${oneUp}Type`]) {
                        copyDamagedRoomToId(oneUp, counter);
                        RoomCleanup(setFieldValue, oneUp);
                    }
                    counter++;
                }
            }
        });
    };

    editIds.map((editId) => {
        if (values[`room${editId}Type`]) {
            rows.push(createData(`${values[`room${editId}Type`]}`, `${values[`room${editId}Area`]}`, true, editId));
        }
    });

    return (
        <>
            <TableContainer component={Paper}>
                {rows.length > 0 && (
                    <Table sx={{ minWidth: 230 }} aria-label="customized table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>{translations.room.TYPE}</StyledTableCell>
                                <StyledTableCell align="right">{translations.room.AREA}</StyledTableCell>
                                <StyledTableCell align="right"> </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <StyledTableRow key={row.type}>
                                    <StyledTableCell component="th" scope="row">
                                        {
                                            values.roomTypesList?.filter((roomType: GenericObject) => {
                                                return roomType.id == row.type;
                                            })[0].naziv
                                        }
                                    </StyledTableCell>
                                    <StyledTableCell align="right">{row.area}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        {row.editable && (
                                            <>
                                                <IconButton
                                                    id="editDamagedRoom"
                                                    onClick={() => handleEditDamagedRoom(row.editId)}
                                                    aria-label="edit"
                                                    disabled={inputRoomOpen}
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    id="deleteDamagedRoom"
                                                    onClick={() => {
                                                        setDeleteId(row.editId);
                                                        setConfirmOpen(true);
                                                    }}
                                                    aria-label="delete"
                                                    disabled={inputRoomOpen}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <ConfirmationDialog
                id="damagedRoomDialog"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={() => handleDeleteDamagedRoom(deleteId)}
            >
                {translations.room.CONFIRM_DELETE}
            </ConfirmationDialog>
        </>
    );
};

export default DamagedRoomsTable;
